import { create } from 'zustand';

import {
  STATUS_ORDER,
  bandForPriority,
  type BoardSnapshot,
  type JobId,
  type Task,
  type TaskPatch,
  type TaskStatus,
} from '@ai-studio/shared';

import { ApiError, api } from '../lib/api.js';

interface BoardState {
  board: BoardSnapshot | null;
  tasks: Task[];
  error: string | null;
  loading: boolean;
  /** Job filter; null means every job. */
  jobFilter: JobId | null;

  load: (projectId: number) => Promise<void>;
  create: (
    projectId: number,
    input: {
      title: string;
      slug?: string;
      description?: string;
      job: JobId;
      priority: number;
      status?: TaskStatus;
      createdBy?: string;
    },
  ) => Promise<Task | null>;
  patch: (projectId: number, taskId: number, patch: TaskPatch) => Promise<boolean>;
  remove: (projectId: number, taskId: number) => Promise<boolean>;
  setJobFilter: (job: JobId | null) => void;
  upsertLocal: (task: Task) => void;
}

const EMPTY_BOARD: BoardSnapshot = {
  backlog: [],
  ready: [],
  inProgress: [],
  review: [],
  done: [],
  blockedCount: 0,
};

export const useBoard = create<BoardState>((set) => ({
  board: EMPTY_BOARD,
  tasks: [],
  error: null,
  loading: false,
  jobFilter: null,

  load: async (projectId) => {
    set({ loading: true, error: null });
    try {
      const result = await api.get<{ board: BoardSnapshot; tasks: Task[] }>(
        `/projects/${projectId}/tasks`,
      );
      set({ board: result.board, tasks: result.tasks, loading: false });
    } catch (error) {
      set({
        loading: false,
        error: error instanceof ApiError ? error.message : 'Could not load the board.',
      });
    }
  },

  create: async (projectId, input) => {
    try {
      const result = await api.post<{ task: Task }>(`/projects/${projectId}/tasks`, input);
      set((state) => ({ tasks: [...state.tasks, result.task] }));
      // Board columns are derived from the task list so the UI cannot drift.
      set((state) => ({ board: regroup(state.tasks, state.jobFilter) }));
      return result.task;
    } catch (error) {
      set({ error: error instanceof ApiError ? error.message : 'Could not create the task.' });
      return null;
    }
  },

  patch: async (projectId, taskId, patch) => {
    try {
      const result = await api.patch<{ task: Task }>(
        `/projects/${projectId}/tasks/${taskId}`,
        patch,
      );
      set((state) => {
        const tasks = state.tasks.map((t) => (t.id === taskId ? result.task : t));
        return { tasks, board: regroup(tasks, state.jobFilter) };
      });
      return true;
    } catch (error) {
      set({ error: error instanceof ApiError ? error.message : 'Could not update the task.' });
      return false;
    }
  },

  remove: async (projectId, taskId) => {
    try {
      await api.del(`/projects/${projectId}/tasks/${taskId}`);
      set((state) => {
        const tasks = state.tasks.filter((t) => t.id !== taskId);
        return { tasks, board: regroup(tasks, state.jobFilter) };
      });
      return true;
    } catch (error) {
      set({ error: error instanceof ApiError ? error.message : 'Could not delete the task.' });
      return false;
    }
  },

  setJobFilter: (jobFilter) => {
    set((state) => ({ jobFilter, board: regroup(state.tasks, jobFilter) }));
  },

  /** Apply a task change pushed over the event socket. */
  upsertLocal: (task) => {
    set((state) => {
      const exists = state.tasks.some((t) => t.id === task.id);
      const tasks = exists ? state.tasks.map((t) => (t.id === task.id ? task : t)) : [...state.tasks, task];
      return { tasks, board: regroup(tasks, state.jobFilter) };
    });
  },
}));

/** Rebuild the column snapshot, optionally filtered to a single job. */
function regroup(tasks: Task[], jobFilter: JobId | null): BoardSnapshot {
  const visible = jobFilter ? tasks.filter((t) => t.job === jobFilter) : tasks;
  const columns: Record<TaskStatus, Task[]> = {
    backlog: [],
    ready: [],
    in_progress: [],
    review: [],
    done: [],
  };
  let blockedCount = 0;

  for (const task of visible) {
    if (task.blocked) blockedCount += 1;
    columns[task.status].push(task);
  }
  // Same ordering the server uses: priority desc, then oldest first.
  for (const status of STATUS_ORDER) {
    columns[status].sort((a, b) =>
      a.priority !== b.priority ? b.priority - a.priority : a.createdAt.localeCompare(b.createdAt),
    );
  }

  return {
    backlog: columns.backlog,
    ready: columns.ready,
    inProgress: columns.in_progress,
    review: columns.review,
    done: columns.done,
    blockedCount,
  };
}

export { bandForPriority };